import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users as UsersApi, DealContacts } from "@/api/endpoints";
import type { ActionItemCreate, ActionItemDirection } from "@/api/types";

interface Props {
  dealId: string;
  parentActivityId?: string;
  onSubmit: (payload: ActionItemCreate) => void;
  submitting: boolean;
  onCancel: () => void;
}

export default function ActionItemForm({ dealId, parentActivityId, onSubmit, submitting, onCancel }: Props) {
  const [direction, setDirection] = useState<ActionItemDirection>("us_to_them");
  const [text, setText] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [assigneeContactId, setAssigneeContactId] = useState("");

  const usersQuery = useQuery({ queryKey: ["users"], queryFn: () => UsersApi.list() });
  const contactsQuery = useQuery({
    queryKey: ["deal-contacts", dealId],
    queryFn: () => DealContacts.list(dealId),
  });

  const submit = () => {
    if (!text.trim() || submitting) return;
    onSubmit({
      direction,
      text: text.trim(),
      due_date: dueDate || null,
      parent_activity_id: parentActivityId ?? null,
      assignee_user_id: direction === "us_to_them" ? assigneeUserId || null : null,
      assignee_contact_id: direction === "them_to_us" ? assigneeContactId || null : null,
    });
  };

  return (
    <div className="panel-tight p-3 space-y-2">
      <div className="flex items-center gap-2">
        <div className="inline-flex items-center gap-0.5 rounded-md border border-fg/[0.08] p-0.5">
          {(["us_to_them", "them_to_us"] as const).map((d) => (
            <button
              key={d}
              type="button"
              className={`px-3 py-1 rounded text-sm font-semibold uppercase tracking-wider transition ${
                direction === d
                  ? "bg-accent-azure/10 text-accent-azure"
                  : "text-mut-300 hover:text-mut-100 hover:bg-fg/[0.04]"
              }`}
              onClick={() => setDirection(d)}
            >
              {d === "us_to_them" ? "Us" : "Them"}
            </button>
          ))}
        </div>
        <span className="text-xs text-mut-500">
          {direction === "us_to_them"
            ? "Our task — assign a teammate"
            : "Waiting on the client — assign a contact"}
        </span>
      </div>
      <input
        className="input w-full"
        placeholder="What needs to happen?"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
      />
      <div className="flex gap-2">
        <input
          type="date"
          className="input w-40"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          aria-label="Due date"
        />
        {direction === "us_to_them" ? (
          <select
            className="input w-48"
            value={assigneeUserId}
            onChange={(e) => setAssigneeUserId(e.target.value)}
            aria-label="Assign to teammate"
          >
            <option value="">Unassigned</option>
            {(usersQuery.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        ) : (
          <select
            className="input w-48"
            value={assigneeContactId}
            onChange={(e) => setAssigneeContactId(e.target.value)}
            aria-label="Assign to contact"
          >
            <option value="">Unassigned</option>
            {(contactsQuery.data ?? []).map((dc) => (
              <option key={dc.contact.id} value={dc.contact.id}>
                {dc.contact.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" className="btn btn-ghost text-sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary text-sm" disabled={!text.trim() || submitting} onClick={submit}>
          Add
        </button>
      </div>
    </div>
  );
}
